"use strict";

import {HouseOt, isMsgUpdater, ListMsgOt, MsgOt, OHouseOtConfig, OtId} from "lib/edit/ot/houseOt";
import {FactoryRegistry, House, InitSlaveRep, IPublicDoor, Msg, ResetStatesMsg, State} from "lib/edit/ot/urban";


/**
 * Implémentation d'une {House} de type Operationnal Transformation pour gérer l'édition d'une simple String.
 */

interface OStringHouseConfig extends OHouseOtConfig {
	initialText?: string;
}

class StringHouse extends HouseOt {

	protected _body: StrBodyState;
	protected _states: Array<State> = [];

	/**
	 * @param options Options supplémentaires :
	 *    - initialText
	 */
	constructor(options: OStringHouseConfig = {}) {
		super(options);
		this._body = new StrBodyState().init("body", options.initialText).attachHouse(this);
	}

	get text() {
		return this._body ? this._body.str : null
	}

	insertText(start: number, insText: string, fromDoor?: IPublicDoor) {
		if (start < 0 || start > this.text.length) throw Error(`Incorrect start insert: ${start} / ${this.text.length}`);
		(fromDoor || this.publicDoor).receiveMsg(new StrInsertMsg().init(start, insText));
	}

	deleteText(start: number, lengthOrDelText: number | string, fromDoor?: IPublicDoor) {
		if (start < 0 || start > this.text.length) throw Error(`Incorrect start delete: ${start} / ${this.text.length}`);
		if (typeof lengthOrDelText === "string") {
			if (this.text.substr(start, lengthOrDelText.length) !== lengthOrDelText) throw Error(`Incorrect delete text ::'${lengthOrDelText}' at start ${start} != ${this.text}`);
			lengthOrDelText = lengthOrDelText.length;
		} else {
			if (lengthOrDelText <= 0) throw `Incorrect delete length: ${lengthOrDelText}`;
			if (this.text.length < start + lengthOrDelText) throw Error(`Incorrect delete  : start ${start} + length ${lengthOrDelText} exceed text length ${this.text.length}`);
			//lengthOrDelText = this.text.substr(start, lengthOrDelText);
		}
		(fromDoor || this.publicDoor).receiveMsg(new StrDeleteMsg().init(start, lengthOrDelText));
	}

	moveText(start: number, length: number, target: number, fromDoor?: IPublicDoor) {
		if (start < 0 || start > this.text.length) throw Error(`Incorrect start move: ${start} / ${this.text.length}`);
		if (length < 0 || start + length > this.text.length) throw Error(`Incorrect delete range: ${start} + ${length} / ${this.text.length}`);
		if (target < 0 || target > this.text.length) throw Error(`Incorrect target move: ${start} / ${this.text.length}`);
		const str = this.text.substr(start, length);
		(fromDoor || this.publicDoor).receiveMsg(new StrMoveMsg().init(this.id, start, str, target));
	}


	getStateById<T extends State>(id: string): T {
		if (id === "body") return this._body as unknown as T;
		for (let i = 0, e = this._states.length; i < e; i++) if (this._states[i].id == id) return this._states[i] as T;
		return null;
	}

	/**
	 * Enregistre un state dans la house pour être synchronisé avec les autres houses.
	 *
	 * Attention: si la house est déjà connectée, aucune gestion de dispatch de Msg à ce niveau.
	 * Un envoi explicite de Msg via la publicDoor doit être effectué pour informer de ce nouveau State.
	 *
	 * @param state State qui DOIT posseder un id unique parmi toutes le houses synchronisées.
	 */
	putState(state: State) {
		if (state.id === 'body') {
			this._body = state as StrBodyState;
		} else {
			this._states.push(state.attachHouse(this));
		}
	}

	/**
	 * Ajoute un state dans la house qui sera synchronisé avec les autres houses.
	 * Un id unique va lui être affecté automatiquement.
	 *
	 * @param state State sans id affecté (il sera écrasé).
	 * @return Promise(State) lorsque ce state est opérationnel avec son id.
	 */
	addState(state: State) {
		//TODO générateur d'id par le masterRoot.
		throw "TODO";
	}

	removeState(state: State) {
		if (state === this._body) this._body = null;
		for (let i = 0, e = this._states.length; i < e; i++) if (state === this._states[i]) this._states.splice(i, 1);
	}

	xApplyMsgOtLocally(msg: MsgOt, updatable: boolean): MsgOt {
		try {
			if (this._body) this._body.update(msg);
			for (let i = 0, e = this._states.length; i < e; i++) this._states[i].update(msg);
		} catch (e) {
			console.error("xApplyMsgOtLocally in house failed", this, msg, e);
			throw e;
		}
		return msg;
	}

	xApplyResetStatesLocally(msg: InitSlaveRep | ResetStatesMsg) {
		super.xApplyResetStatesLocally(msg);
		if (this._body) this._body.update(msg);
		this._states = msg.datas.states.map((st: State) => House.STATE_FACTORYREG.newFromJson(st));
	}

	writeFullStates(datas: any, options?: any) {
		datas.body = this._body.str;
		datas.states = this._states.map(st => st.asJson());
	}
}

/**
 * Mémoire du contenu stocké dans la house.
 */
class StrBodyState extends State {
	static readonly type = "strBody";

	house: StringHouse;
	str: string;

	init(id: string, string: string) {
		this.id = id;
		this.str = string;
		if (StringHouse.DEBUG && this.house) console.log("Body " + this.house + " inited :: " + this.str);
		return this;
	}

	update(msg: Msg) {
		const f = (this as any)[msg.type];
		if (f) {
			f.call(this, msg);
		} else if (isMsgUpdater(msg)) {
			msg.applyUpdates(this);
		}
		if (StringHouse.DEBUG) console.log("Body " + this.house + " :: " + this.str);
	}

	protected strIns(msg: StrInsertMsg) {
		if (StringHouse.ASSERT) if (this.house) msg.assertMsgValidity(this.house);
		this.str = this.str.substring(0, msg.start) + msg.txt + this.str.substring(msg.start);
	}

	protected strDel(msg: StrDeleteMsg) {
		if (StringHouse.ASSERT) if (this.house) msg.assertMsgValidity(this.house);
		//PERF if (msg.txt == null) quand stabilisé, recacullé que si msg.txt nullifié
		msg.setText(this.str.substr(msg.start, msg.len)); //recalcul du texte supprimé (après adjust)
		this.str = this.str.substring(0, msg.start) + this.str.substring(msg.end);
	}

	protected resetStates(msg: ResetStatesMsg) {
		this.str = msg.datas.body;
	}

	protected initSlaveRep(msg: InitSlaveRep) {
		this.str = msg.datas.body;
	}

	toJson(json: any) {
		super.toJson(json);
		json.str = this.str;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg);
		this.str = json.str;
	}

}

House.STATE_FACTORYREG.register(StrBodyState);

/**
 * Mémoire d'un range stockable dans la house. Les metas permettent de qualifier ce StrRangeState
 * (type, propriétaire, contenu d'une annotation associée...)
 */
class StrRangeState extends State {
	static readonly type = "strRg";

	start: number;
	end: number;

	init(id: string, metas: any, start?: number, end?: number) {
		this.id = id;
		this.metas = metas;
		this.start = start || 0;
		this.end = end || 0;
		return this;
	}

	/** Pour changer le range et diffuser ces modifs dans les houses. */
	set({start, end, metas}: { start: number, end: number, metas: any }, fromDoor?: IPublicDoor) {
		(fromDoor || this.house.publicDoor).receiveMsg(new StrRangeUpdtMsg().init(this.id, start, end, metas));
	}

	update(msg: Msg) {
		const f = (this as any)[msg.type];
		if (f) {
			f.call(this, msg);
		} else if (isMsgUpdater(msg)) {
			msg.applyUpdates(this);
		}
	}

	protected strRg(msg: StrRangeUpdtMsg) {
		if (msg.rgId === this.id) {
			if (msg.rgStart >= 0) this.start = msg.rgStart;
			if (msg.rgEnd >= 0) this.end = msg.rgEnd;
			if (msg.rgMetas) this.metas = msg.rgMetas;
		}
	}

	protected strIns(msg: StrInsertMsg) {
		if (this.start >= msg.start) this.start += msg.len;
		if (this.end >= msg.start) this.end += msg.len;
	}

	protected strDel(msg: StrDeleteMsg) {
		if (this.start > msg.start) {
			if (this.start > msg.start + msg.len) this.start -= msg.len;
			else this.start = msg.start;
		}
		if (this.end > msg.start) {
			if (this.end > msg.start + msg.len) this.end -= msg.len;
			else this.end = msg.start;
		}
	}

	toJson(json: any) {
		super.toJson(json);
		json.start = this.start;
		json.end = this.end;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<State>) {
		super.fromJson(json, factoryReg);
		this.start = json.start || 0;
		this.end = json.end || 0;
	}

}

House.STATE_FACTORYREG.register(StrRangeState);


/**
 * Interface pour tous les messages isProgressSensitive de cette StringHouse.
 * Contient toutes les opérations qu'un message est susceptible de subir
 * comme changement dû à d'autres messages concurents.
 */
interface IStrMsg extends MsgOt {

	/**
	 * Le msg courant se placera après dans la pile d'historique définitif, une insertion par le msg courant
	 * au même offset que le déplacement spécifié en paramètre se placera après.
	 */
	adjustInsert(start: number, len: number): void;

	/**
	 * Pour undo/redo. Le msg courant issu de l'inversion du msg à annuler ou refaire traverse une insertion pour atteindre
	 * la tête de la pile des msg suivants avant de pouvoir être exécuté/diffusé au master.
	 * @param invertOfOtId Id du MsgOt dont ce message est l'inversion (undo ou redo).
	 */
	crossInsert(start: number, len: number, invertOfOtId: OtId): void;

	/**
	 * Le msg courant est reculé dans son propre historique. S'il s'agit d'une insertion, il doit mémoriser dans une pile
	 * si il doit se placer après ce déplacement spécifié en paramètre lorsqu'il sera rejoué (forward).
	 */
	rewindInsert(start: number, len: number): void;

	/**
	 * Le msg courant est réavancé, S'il s'agit d'une insertion, il doit retrouver l'information dans sa pile pour savoir
	 * si il doit être placé avant ou après ce déplacement spécifié en paramètre.
	 */
	forwardInsert(start: number, len: number): void;

	/**
	 * Le msg courant se placera après dans la pile d'historique définitif, une insertion par le msg courant
	 * au même offset que le déplacement spécifié en paramètre se placera après.
	 * @param start
	 * @param dLen dLen Toujours <0
	 */
	adjustDelete(start: number, dLen: number): void;

	/**
	 * Pour undo/redo. Le msg courant issu de l'inversion du msg à annuler ou refaire traverse une suppression pour atteindre
	 * la tête de la pile des msg suivants avant de pouvoir être être exécuté/diffusé au master.
	 * @param start
	 * @param dLen Toujours <0
	 * @param fromOtId
	 */
	crossDelete(start: number, dLen: number, fromOtId: OtId): void;

	/**
	 * Le msg courant est reculé dans son propre historique. S'il s'agit d'une insertion, il doit mémoriser dans une pile
	 * si il doit se placer après ce déplacement spécifié en paramètre lorsqu'il sera rejoué (forward).
	 * @param start
	 * @param dLen Toujours <0
	 */
	rewindDelete(start: number, dLen: number): void;

	/**
	 * Le msg courant est réavancé, S'il s'agit d'une insertion, il doit retrouver l'information dans sa pile pour savoir
	 * si il doit être placé avant ou après ce déplacement spécifié en paramètre.
	 * @param start
	 * @param dLen Toujours <0
	 */
	forwardDelete(start: number, dLen: number): void;
}

/**
 * {
 * houseId : HouseId
 * start: 23,
 * txt: "txtToInsert"
 * hist: mémoire à chaque rewind du cas particulier où les deux start sont égaux.
 * }
 *
 */
class StrInsertMsg extends MsgOt implements IStrMsg {
	static readonly type = "strIns";

	start: number;
	txt: string;

	protected mem: Array<any>;
	protected _mem: Dict<any>;

	init(start: number, insText: string): this {
		this.start = start;
		this.txt = insText;
		return this;
	}

	get isUndoable() {
		return true
	}

	get isBodyMutator() {
		return true
	}

	get killed() {
		return this.txt.length === 0
	}

	kill() {
		this.txt = ""
	}

	get len() {
		return this.txt.length
	}

	get end() {
		return this.start + this.len
	}

	adjust(msg: IStrMsg) {
		msg.adjustInsert(this.start, this.len); //TODO transformer api et passer this + hook en param pour msg de type List.
	}

	rewind(msg: IStrMsg) {
		msg.rewindInsert(this.start, this.len);
	}

	forward(msg: IStrMsg) {
		msg.forwardInsert(this.start, this.len);
	}

	cross(msg: IStrMsg, invertOfOtId: OtId) {
		msg.crossInsert(this.start, this.len, invertOfOtId);
	}

	mergeWith(nextMsg: MsgOt): MsgOt {
		switch (nextMsg.type) {
		case StrInsertMsg.type :
			if ((nextMsg as StrInsertMsg).start == this.start + this.len) {
				this.txt += (nextMsg as StrInsertMsg).txt;
				return this;
			}
			break;
		case StrDeleteMsg.type :
			if ((nextMsg as StrDeleteMsg).start + (nextMsg as StrDeleteMsg).len == this.start + this.len) {
				const newLen = this.len - (nextMsg as StrDeleteMsg).len;
				if (newLen >= 0) {
					this.txt = this.txt.substring(0, newLen);
					return this;
				}
			}
			break;
		}
		return null;
	}

	adjustDelete(start: number, dLen: number) {
		if (StringHouse.ASSERT) if (dLen > 0) throw Error(`dLen > 0!`);
		if (start < this.start) this.start = Math.max(this.start + dLen, start);
	}

	crossDelete(start: number, dLen: number, fromOtId: OtId) {
		//(~= rewindInsert pour la mémoire + forwardDelete pour la transformation)
		//on mémorise les écarts (en cas d'op inverse ultérieure)
		if (!this._mem) this._mem = Object.create(null);
		if (this.start <= start) {
			this._mem[fromOtId] = pushPosInsIns([], 'b');
		} else if (this.start >= start - dLen) {
			this._mem[fromOtId] = pushPosInsIns([], 'a');
		} else {
			this._mem[fromOtId] = pushPosInsIns([], 'i', this.start - start);
		}
		this.adjustDelete(start, dLen);
	}

	rewindDelete(start: number, dLen: number) {
		this.adjustInsert(start, -dLen);
	}

	forwardDelete(start: number, dLen: number) {
		this.adjustDelete(start, dLen);
	}

	adjustInsert(start: number, len: number) {
		if (StringHouse.ASSERT) if (len < 0) throw Error(`len < 0!`);
		if (start <= this.start) this.start += len;
	}

	crossInsert(start: number, len: number, invertOfOtId: OtId) {
		//(~= rewindDelete pour la mémoire + forwardInsert pour la transformation)
		if (this._mem && invertOfOtId != null) {
			const memArray = this._mem[invertOfOtId];
			if (memArray != null) {
				delete this._mem[invertOfOtId];
				this.xForwardInsert(start, len, memArray);
				return;
			}
		}
		this.adjustInsert(start, len);
	}

	rewindInsert(start: number, len: number) {
		//on mémorise la situation pour le forward
		if (this.start <= start) {
			this.mem = pushPosInsIns(this.mem, 'b');
		} else if (this.start >= start + len) {
			this.mem = pushPosInsIns(this.mem, 'a');
		} else {
			this.mem = pushPosInsIns(this.mem, 'i', this.start - start);
		}
		this.adjustDelete(start, -len);
	}

	forwardInsert(start: number, len: number) {
		this.xForwardInsert(start, len, this.mem);
	}

	xForwardInsert(start: number, len: number, memArray: Array<any>) {
		const mem = popPosInsIns(memArray);
		if (mem.pos === 'b') {			//cas 1
			if (start < this.start) this.start += len;
		} else if (mem.pos === 'a') {		//cas 3
			if (start <= this.start) this.start += len;
		} else {						//cas 2
			//notre insertion est dans cet insert, en cas de move on repart de start et non this.start.
			this.start = start + mem.startOffset;
		}
	}

	invert() {
		return new StrDeleteMsg().init(this.start, this.len).copyStableMetasFrom(this);
	}

	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	assertMsgValidity(house: StringHouse) {
		const str = house.text;
		if (this.start < 0 || this.start > str.length) throw Error(`Start out of bound for ${this} in : '${str}'`);
	}

	toJson(json: any) {
		super.toJson(json);
		json.start = this.start;
		json.txt = this.txt;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.start = json.start;
		this.txt = json.txt;
		if (json.mem !== undefined) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.start = o.start;
		this.txt = o.txt;
		if (o.mem !== undefined && purpose !== 'hist') this.mem = o.mem ? o.mem.concat() : null; //utile hors purpose='hist' ?
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées (txt...)
		const json = this.asJson();
		json.len = this.len; //pour move (txt null).
		if (this.otId) json.otId = this.otId;
		if (this._mem !== undefined) json._mem = this._mem;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(StrInsertMsg);


/**
 * {
 * start: 23,
 * len: 5
 * txt: "rem text utilisé en local pour le undo/redo"
 * }
 * note : la mémoire du texte supprimé est obligatoire pour la fonction undo/redo via invert().
 */
class StrDeleteMsg extends MsgOt implements IStrMsg {
	static readonly type = "strDel";

	start: number;
	len: number;

	protected mem: Array<any>;
	protected _mem: Dict<any>;
	protected txt: string;

	init(start: number, len: number) {
		this.start = start;
		this.len = len;
		return this;
	}

	/** mémoire du text supprimé pour construire un message inversé d'insertion. */
	setText(text: string) {
		this.txt = text;
		return this;
	}

	get isUndoable() {
		return this.txt != null
	}

	get isBodyMutator() {
		return true
	}

	get end() {
		return this.start + this.len
	}

	get killed() {
		return this.len <= 0
	}

	kill() {
		this.xSetLen(0);
	}

	xSetLen(len: number) {
		this.len = len;
		this.txt = null; //le txt devient faux, il sera recalculé à l'update du body.
	}

	adjust(msg: IStrMsg) {
		msg.adjustDelete(this.start, -this.len);//TODO transformer api et passer this + hook en param pour msg de type List.
	}

	rewind(msg: IStrMsg) {
		msg.rewindDelete(this.start, -this.len);
	}

	forward(msg: IStrMsg) {
		msg.forwardDelete(this.start, -this.len);
	}

	cross(msg: IStrMsg, invertOfOtId: OtId) {
		msg.crossDelete(this.start, -this.len, this.otId);
	}

	mergeWith(nextMsg: MsgOt): MsgOt {
		if (this.txt == null) return null;
		switch (nextMsg.type) {
		case StrDeleteMsg.type :
			if ((nextMsg as StrDeleteMsg).start == this.start) {
				//ex : Suppr + Suppr
				this.len = this.txt.length;
				this.txt += (nextMsg as StrDeleteMsg).txt;
				return this;
			}
			if ((nextMsg as StrDeleteMsg).start + (nextMsg as StrDeleteMsg).len == this.start) {
				//ex : Backspace + Backspace
				(nextMsg as StrDeleteMsg).len = (nextMsg as StrDeleteMsg).txt.length;
				(nextMsg as StrDeleteMsg).txt += this.txt;
				return nextMsg;
			}
			break;
		}
		return null;
	}

	adjustDelete(start: number, dLen: number) {
		this.xAdjustDelete(start, dLen);
	}

	xAdjustDelete(start: number, dLen: number) {
		if (StringHouse.ASSERT) if (dLen > 0) throw Error(`dLen > 0!`);
		const otherEnd = start - dLen;
		const myEnd = this.end;
		if (otherEnd > this.start && start < myEnd) this.xSetLen(Math.max(0, start - this.start) + Math.max(0, myEnd - otherEnd));
		if (start < this.start) this.start = Math.max(this.start + dLen, start);
	}

	crossDelete(start: number, dLen: number, fromOtId: OtId) {
		//(~= rewindInsert pour la mémoire + forwardDelete pour la transformation)
		const otherEnd = start - dLen;
		const myEnd = this.end;
		//on mémorise les écarts (en cas d'op inverse ultérieure)
		if (!this._mem) this._mem = Object.create(null);
		this._mem[fromOtId] = pushPosInsDel([],
			this.start < start, this.start >= otherEnd, this.start - start,
			myEnd <= start, myEnd > otherEnd, otherEnd - myEnd);
		this.xAdjustDelete(start, dLen);
	}

	rewindDelete(start: number, dLen: number) {
		this.xAdjustInsert(start, -dLen);
	}

	forwardDelete(start: number, dLen: number) {
		this.xAdjustDelete(start, dLen);
	}

	adjustInsert(start: number, len: number) {
		this.xAdjustInsert(start, len);
	}

	xAdjustInsert(start: number, len: number) {
		if (StringHouse.ASSERT) if (len < 0) throw Error(`len < 0!`);
		if (start <= this.start) {
			//insertion avant notre suppr
			this.start += len;
		} else {
			if (start < this.end) this.xSetLen(this.len + len); //insertion dans notre suppr, on absorbe.
		}
	}

	crossInsert(start: number, len: number, invertOfOtId: OtId) {
		//(~= rewindDelete pour la mémoire + forwardInsert pour la transformation)
		if (this._mem && invertOfOtId != null) {
			const memArray = this._mem[invertOfOtId];
			if (memArray) {
				delete this._mem[invertOfOtId];
				this.xForwardInsert(start, len, memArray);
				return;
			}
		}
		this.xAdjustInsert(start, len);
	}

	rewindInsert(start: number, len: number) {
		const otherEnd = start + len;
		const myEnd = this.end;
		//on mémorise les écarts internes pour le forward
		this.mem = pushPosInsDel(this.mem,
			this.start < start, this.start >= otherEnd, this.start - start,
			myEnd <= start, myEnd > otherEnd, otherEnd - myEnd);
		this.xAdjustDelete(start, -len);
	}

	forwardInsert(start: number, len: number) {
		this.xForwardInsert(start, len, this.mem);
	}

	xForwardInsert(start: number, len: number, memArray: Array<any>) {
		const mem = popPosInsDel(memArray);
		if (mem.startAfter) { //cas 1 (cf slide dans archi.odp)
			//insertion avant notre suppr : c'est le start qu'on décale
			this.start += len;
		} else if (mem.endBefore) { //cas 2
			//insertion après notre suppr, rien à faire
		} else if (mem.startBefore) { // cas 3
			if (mem.endAfter) { // cas 3.1
				//notre suppr enveloppe totalement l'insertion
				this.xSetLen(this.len + len);
			} else { //cas 3.2
				//notre suppr commence avant mais termine dans l'insertion
				this.xSetLen(len - mem.endOffset + start - this.start);
			}
		} else { // cas 4
			//notre suppr commence dans l'insertion...
			this.start = start + mem.startOffset;
			if (mem.endAfter) { //cas 4.1
				///... et termine après l'insertion
				this.xSetLen(this.len + len - mem.startOffset);
			} else { //cas 4.2
				///... et termine dans l'insertion
				this.xSetLen(this.len + len - mem.startOffset - mem.endOffset);
			}
		}
	}

	invert() {
		if (this.txt == null) throw "txt not available for undo/redo";
		return new StrInsertMsg().init(this.start, this.txt).copyStableMetasFrom(this);
	}


	inMigration(inMigr: boolean) {
		if (!inMigr) {
			delete this.mem;
			delete this._mem;
		}
	}

	assertMsgValidity(house: StringHouse) {
		const str = house.text;
		if (this.len < 0) throw Error(`Invalid length in ${this}`);
		if (this.start < 0 || this.start > str.length) throw Error(`Start out of bound for ${this} in : '${str}'`);
		const end = this.end;
		if (end < 0 || end > str.length) throw Error(`End '${end}' out of bound for ${this} in : '${str}'`);
		if (this.txt != null && this.txt.length !== this.len) throw Error(`msg.len and msg.txt prorpeties are out of sync in ${this}`);
	}

	toJson(json: any) {
		super.toJson(json);
		json.start = this.start;
		json.len = this.len;
		if (this.mem) json.mem = this.mem;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.start = json.start;
		this.len = json.len;
		if (json.mem !== undefined) this.mem = json.mem;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.start = o.start;
		this.len = o.len;
		if (purpose === 'hist') {
			this.txt = o.txt;
		} else {
			if (o.mem !== undefined) this.mem = o.mem ? o.mem.concat() : null; //utile hors purpose='hist' ?
		}
	}

	toString() {
		//pour debug, on publie aussi les props non sérailisées (txt...)
		const json = this.asJson();
		json.txt = this.txt;
		if (this.otId) json.otId = this.otId;
		if (this._mem !== undefined) json._mem = this._mem;
		return JSON.stringify(json);
	}
}

House.MSG_FACTORYREG.register(StrDeleteMsg);

/**
 * Liste de Msg implémentant l'api StrMsg.
 */
class StrListMsgOt extends ListMsgOt implements IStrMsg {
	static readonly type: string = "strList";

	msgs: Array<IStrMsg>;

	adjustInsert(start: number, len: number) {
		this.msgs.forEach(m => {
			m.adjustInsert(start, len)
		});
	}

	crossInsert(start: number, len: number, invertOfOtId: OtId) {
		this.msgs.forEach(m => {
			m.crossInsert(start, len, invertOfOtId)
		});
	}

	rewindInsert(start: number, len: number) {
		this.msgs.forEach(m => {
			m.rewindInsert(start, len)
		});
	}

	forwardInsert(start: number, len: number) {
		this.msgs.forEach(m => {
			m.forwardInsert(start, len)
		});
	}

	adjustDelete(start: number, dLen: number) {
		this.msgs.forEach(m => {
			m.adjustDelete(start, dLen)
		});
	}

	crossDelete(start: number, dLen: number, fromOtId: OtId) {
		this.msgs.forEach(m => {
			m.crossDelete(start, dLen, fromOtId)
		});
	}

	rewindDelete(start: number, dLen: number) {
		this.msgs.forEach(m => {
			m.rewindDelete(start, dLen)
		});
	}

	forwardDelete(start: number, dLen: number) {
		this.msgs.forEach(m => {
			m.forwardDelete(start, dLen)
		});
	}

}

House.MSG_FACTORYREG.register(StrListMsgOt);


/**
 * Implémentation d'un move par un simple enchainement d'un delete et d'un insert.
 *
 * ATTENTION pas satisfaisant en asynch dans le cas de suppr ou autre move concurrent :
 * les adjust intermédiaires peuvent faire évoluer la zone supprimée et le report de ces changements n'est pas réalisée
 * dans la zone insérée.
 * Cette prise en compte de la variation de la supp sur l'ins est complexe à cause du mécanisme de rewind/forward
 * qui ne peut admettre de variation de son contenu inséré (les offsets dans la mem du rewind ne suivent pas ces ajustements).
 *
 */
class StrMoveMsg extends StrListMsgOt {
	static readonly type = "strMove";

	init(houseId: string, start: number, text: string, target: number) {
		return super.initList([
			new StrDeleteMsg().init(start, text.length),
			new StrInsertMsg().init(target > start ? target - text.length : target, text)
		]);
	}
}

House.MSG_FACTORYREG.register(StrMoveMsg);

/**
 * Message de modification d'un StrRangeState
 */
class StrRangeUpdtMsg extends MsgOt implements IStrMsg {
	static readonly type = "strRg";

	rgId: string;
	rgStart: number;
	rgEnd: number;
	rgMetas: any;

	init(rangeId: string, start: number, end: number, metas?: any) {
		this.rgId = rangeId;
		if (start >= 0) this.rgStart = start;
		if (end >= 0) this.rgEnd = end;
		if (metas) this.rgMetas = metas;
		return this;
	}

	get killed() {
		return false;
	}

	kill() {
	}

	get isUndoable(): boolean {
		return false;
	}

	adjustDelete(start: number, dLen: number) {
		//TODO
	}

	crossDelete(start: number, dLen: number, fromOtId: OtId) {
		//TODO
	}

	rewindDelete(start: number, dLen: number) {
		//TODO
	}

	forwardDelete(start: number, dLen: number) {
		//TODO
	}

	adjustInsert(start: number, len: number) {
		//TODO
	}

	crossInsert(start: number, len: number, invertOfOtId: OtId) {
		//TODO
	}

	rewindInsert(start: number, len: number) {
		//TODO
	}

	forwardInsert(start: number, len: number) {
		//TODO
	}

	invert(): StrRangeUpdtMsg {return new StrRangeUpdtMsg().init(this.rgId, this.rgStart, this.rgEnd, this.rgMetas).copyStableMetasFrom(this)}

	toJson(json: any) {
		super.toJson(json);
		json.rgId = this.rgId;
		if (this.rgStart >= 0) json.rgStart = this.rgStart;
		if (this.rgEnd >= 0) json.rgEnd = this.rgEnd;
		if (this.rgMetas) json.rgMetas = this.rgMetas;
	}

	fromJson(json: any, factoryReg: FactoryRegistry<Msg>) {
		super.fromJson(json, factoryReg);
		this.rgId = json.rgId;
		if (json.rgStart >= 0) this.rgStart = json.rgStart;
		if (json.rgEnd >= 0) this.rgEnd = json.rgEnd;
		if (json.rgMetas) this.rgMetas = json.rgMetas;
	}

	protected cloneFrom(o: this, purpose?: string) {
		super.cloneFrom(o, purpose);
		this.rgId = o.rgId;
		if (o.rgStart >= 0) this.rgStart = o.rgStart;
		if (o.rgEnd >= 0) this.rgEnd = o.rgEnd;
		if (o.rgMetas) this.rgMetas = JSON.parse(JSON.stringify(o.rgMetas));
	}
}

House.MSG_FACTORYREG.register(StrRangeUpdtMsg);


/**
 *
 * @param position
 *            'b' : même séquence, startBefore
 *            'a' : même séquence, startAfter
 *            'i" : même séquence, insert imbriqué
 * @param startOffset Utile uniquement si position==='i', mémorise l'offset entre le début de la zone supprimée et le point d'insertion.
 */
function pushPosInsIns(array: Array<any>, position: string, startOffset?: number) {
	if (!array) array = [];
	if (position === 'i') array.push(startOffset);
	array.push(position);
	return array;
}

/**
 * @return Mémoire avec les props : {pos, startOffset}
 */
const _memPosInsInsBuffer: any = {};

function popPosInsIns(array: Array<any>) {
	if (StringHouse.ASSERT) if (array.length === 0) throw Error("Not enough mem for forward");
	const firstVal = array.pop();
	_memPosInsInsBuffer.pos = firstVal;
	if (firstVal === 'i') _memPosInsInsBuffer.startOffset = array.pop();
	return _memPosInsInsBuffer;
}


function pushPosInsDel(array: Array<any>, startBefore: boolean, startAfter: boolean, startOffset: number, endBefore: boolean, endAfter: boolean, endOffset: number) {
	//if(StringHouse.DEBUG) {
	if (!array) array = [];
	array.push(startBefore);
	array.push(startAfter);
	array.push(startOffset);
	array.push(endOffset);
	array.push(endBefore);
	array.push(endAfter);
	return array;
	//} else { version moins lisible optimisée ...}
}

/**
 * @return Mémoire avec les props : {startBefore, startAfter, startOffset, endBefore, endAfter, endOffset}
 */
const _memPosInsDelBuffer: any = {};

function popPosInsDel(array: Array<any>) {
	if (StringHouse.ASSERT) if (array.length === 0) throw Error("Not enough mem for forward");
	//if(StringHouse.DEBUG) {
	_memPosInsDelBuffer.endAfter = array.pop();
	_memPosInsDelBuffer.endBefore = array.pop();
	_memPosInsDelBuffer.endOffset = array.pop();
	_memPosInsDelBuffer.startOffset = array.pop();
	_memPosInsDelBuffer.startAfter = array.pop();
	_memPosInsDelBuffer.startBefore = array.pop();
	return _memPosInsDelBuffer;
	//} else { version moins lisible optimisée ...}
}

export {StringHouse, OStringHouseConfig}
export {StrInsertMsg, StrDeleteMsg, StrMoveMsg}
